import { Fragment } from "react";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";

/**
 * A single breadcrumb entry. `to` marks a link crumb; crumbs without `to`
 * (and always the last crumb) render as the current page (aria-current).
 */
export type PageBreadcrumb = { label: string; to?: string };

/**
 * Shared header breadcrumb used by pages that declare a breadcrumb via
 * `setBreadcrumb`. The visible threshold is responsive: up to 3 crumbs render
 * inline on desktop, 2 on mobile. Beyond the threshold it collapses the middle
 * crumbs into an ellipsis dropdown. Pure presentational component: same crumbs,
 * same JSX. Every visible crumb truncates (max-w-[14rem]) and the container
 * never wraps or overflows.
 */
export const PageBreadcrumbs = ({
  crumbs,
}: {
  crumbs: PageBreadcrumb[];
}) => {
  const isDesktop = useMediaQuery(isDesktopSize);
  const maxVisible = isDesktop ? 3 : 2;
  const collapsed = crumbs.length > maxVisible;

  const renderLinkOrPage = (crumb: PageBreadcrumb, isLast: boolean) => {
    const className = "max-w-[14rem] truncate";
    if (crumb.to && !isLast) {
      return (
        <BreadcrumbLink asChild className={className}>
          <Link to={crumb.to}>{crumb.label}</Link>
        </BreadcrumbLink>
      );
    }
    // BreadcrumbPage renders role="link" aria-disabled per shadcn — the
    // current page is never an anchor.
    return <BreadcrumbPage className={className}>{crumb.label}</BreadcrumbPage>;
  };

  return (
    <Breadcrumb className="min-w-0">
      <BreadcrumbList className="flex-nowrap min-w-0 overflow-hidden">
        {collapsed ? (
          <>
            <BreadcrumbItem>
              {renderLinkOrPage(crumbs[0], false)}
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon-sm" variant="ghost">
                    <BreadcrumbEllipsis />
                    <span className="sr-only">Toggle menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuGroup>
                    {crumbs.slice(1, -1).map((crumb) =>
                      crumb.to ? (
                        <DropdownMenuItem key={crumb.to} asChild>
                          <Link to={crumb.to}>{crumb.label}</Link>
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem key={crumb.label}>
                          {crumb.label}
                        </DropdownMenuItem>
                      ),
                    )}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {renderLinkOrPage(crumbs[crumbs.length - 1], true)}
            </BreadcrumbItem>
          </>
        ) : (
          crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            return (
              <Fragment key={crumb.to ?? crumb.label}>
                <BreadcrumbItem>{renderLinkOrPage(crumb, isLast)}</BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator />}
              </Fragment>
            );
          })
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
};